import { createSelector } from '@reduxjs/toolkit';

import { STATUS } from 'projectConstants';
import {
  analysisOutput,
  chartOutputStatus,
  getActiveTabIndex,
  getBlockMetadata,
  getChartOutputData,
  getIndicatorTableStatus,
  getTableData,
} from 'redux/PlanPageSlice';
import {
  compareAnalysisOutput,
  getChartOutputData as getCompareChartOutputData,
  getCompareScenario,
  getIndicatorTableStatus as getCompareTableStatus,
  getTableData as getCompareTableData,
  scenarioChartOutputStatus,
} from 'redux/ScenarioSlice';

export const selectDefaultTabIndex = createSelector([getBlockMetadata], (metadata) => {
  const index = metadata?.indicators?.findIndex(
    (indicator: { is_default_KPI?: boolean }) => indicator?.is_default_KPI === true,
  );
  return index != null && index > -1 ? index : 0;
});

export const selectSafeActiveIndex = createSelector(
  [analysisOutput, getActiveTabIndex, selectDefaultTabIndex],
  (tabsData, activeTabIndex, defaultTabIndex) => {
    if (!tabsData?.length) return 0;
    if (activeTabIndex >= 0 && activeTabIndex < tabsData.length) return activeTabIndex;
    if (defaultTabIndex > -1 && defaultTabIndex < tabsData.length) return defaultTabIndex;
    return 0;
  },
);

export const selectActiveChartSeries = createSelector(
  [analysisOutput, selectSafeActiveIndex],
  (tabsData, activeIndex) => tabsData?.[activeIndex] ?? null,
);

export const selectCompareDefaultTabIndex = createSelector([compareAnalysisOutput], (tabsData) => {
  const index = tabsData?.findIndex(
    (row: { is_default_KPI?: boolean }) => row?.is_default_KPI === true,
  );
  return index != null && index > -1 ? index : 0;
});

export const selectCompareSafeActiveIndex = createSelector(
  [compareAnalysisOutput, getActiveTabIndex, selectCompareDefaultTabIndex],
  (tabsData, activeTabIndex, defaultTabIndex) => {
    if (!tabsData?.length) return 0;
    if (activeTabIndex >= 0 && activeTabIndex < tabsData.length) return activeTabIndex;
    if (defaultTabIndex > -1 && defaultTabIndex < tabsData.length) return defaultTabIndex;
    return 0;
  },
);

export const selectActiveCompareSeries = createSelector(
  [compareAnalysisOutput, selectCompareSafeActiveIndex],
  (tabsData, activeIndex) => tabsData?.[activeIndex] ?? null,
);

export const selectHasCachedPlanData = createSelector([analysisOutput], (tabsData) =>
  Boolean(tabsData?.length),
);

export const selectHasCachedCompareData = createSelector([compareAnalysisOutput], (tabsData) =>
  Boolean(tabsData?.length),
);

export const selectCompareIndicatorRows = createSelector(
  [compareAnalysisOutput, getCompareChartOutputData, getCompareTableData],
  (tabsData, chartOutput, tableData) => {
    if (Array.isArray(tabsData) && tabsData.length > 0) return tabsData;
    if (Array.isArray(chartOutput) && chartOutput.length > 0) return chartOutput;
    return Array.isArray(tableData) ? tableData : [];
  },
);

export const selectPlanIndicatorRows = createSelector(
  [analysisOutput, getChartOutputData, getTableData],
  (tabsData, chartOutput, tableData) => {
    if (Array.isArray(tabsData) && tabsData.length > 0) return tabsData;
    if (Array.isArray(chartOutput) && chartOutput.length > 0) return chartOutput;
    return Array.isArray(tableData) ? tableData : [];
  },
);

export const selectIsCompareMode = createSelector([getCompareScenario], (compareScenario) =>
  Boolean(compareScenario?.id),
);

export const selectIsPlanRefetching = createSelector(
  [chartOutputStatus, getIndicatorTableStatus],
  (chartStatus, tableStatus) => chartStatus === STATUS.LOADING || tableStatus === STATUS.LOADING,
);

export const selectIsCompareRefetching = createSelector(
  [scenarioChartOutputStatus, getCompareTableStatus],
  (chartStatus, tableStatus) => chartStatus === STATUS.LOADING || tableStatus === STATUS.LOADING,
);

export const selectIsKpiLoading = createSelector(
  [
    selectIsCompareMode,
    selectIsPlanRefetching,
    selectIsCompareRefetching,
    selectPlanIndicatorRows,
    selectCompareIndicatorRows,
  ],
  (isCompareMode, isPlanRefetching, isCompareRefetching, planRows, compareRows) => {
    const isRefetching = isCompareMode ? isCompareRefetching : isPlanRefetching;
    const rows = isCompareMode ? compareRows : planRows;
    return isRefetching && rows.length === 0;
  },
);
